// @flow
import React from 'react';
import { Switch, Route } from 'react-router-dom';
import { isPreRelease } from '@finect/front-utils/isPreRelease';
import { Redirect, PrivateRoute } from '@finect/front-core/Routes';
import { NotFound } from '@finect/tabular-components/NotFound';
import { withTheme } from 'styled-components';
import { CONTENT_URL_MAP, SEGMENTS } from '@finect/content-urls';
import { STOREFRONT_REDIRECTS } from '@finect/tabular-components/HTML';
import { OTHER_TAGS } from './scenes/Storefronts/Personal/data';
import { PlusRoute } from './components/PlusRoute';

import {
  FundSheet,
  FundSheetById,
  ArticleScroll,
  Latest,
  Groups,
  Storefront,
  StorefrontsHome,
  StorefrontProductsList,
  // New Storefronts
  DigitalHome,
  DigitalAcademy,
  AlternativesHome,
  AlternativesAcademy,
  PersonalHome,
  PersonalTag,

  // Segmentos de contenido (hipotecas, seguros, etc.)
  SegmentRoot,
  SegmentArticle,
  SegmentNoticias,
  SegmentVertical,
  SegmentHub,

  // IICs
  ProductSheet,
  CryptoSheet,
  CryptoList,
  ProductComparison,
  FundsHome,
  PlansHome,
  ETFsHome,
  FundsCategories,
  FundsCategory,
  FundsManagementCompanies,
  FundsManagementCompany,
  FundsComparison,
  FundsList,
  WriteReview,
  ProductReview,
  ProductReviews,

  // Indexes
  Euribor,

  // Multimedia
  MultimediaCommons,

  // Favoritos - OLD
  Favorites,
  Panel,
  Feed,
  MyComparisons,
  MyAuthorizations,

  // Favoritos - Finect Plus
  MySelection,
  MySelectionSection,
  MyTools,
  MyAnalysis,
  MyPortfolio,
  SharedPortfolio,
  SponsoredPortfolios,
  MySuperComparison,
  PlusChatbot,
  PlusFAQ,

  // Supercomparador
  SuperComparison,

  // OnBoarding
  Start,
  OnBoarding,

  // Bancos
  BankArticle,
  BankArticleList,
  Bank,
  BankProducts,
  AdvisersFeedback,
  Tag,
  TagsHome,
  ProductQuestions,
  Home,
  Group,
  UserProfileLayout,
  // ProductCheckout,
  ProductSubscription,
  SubscriptionConditions,
  PlusSubscriptionConditions,
  VersionNotes
} from './scenes';

import { BanksSPA } from './scenes/Banks/components';

type Props = {
  theme: Object
};

class AppRouter extends React.Component<Props> {
  render() {
    const { routes } = this.props.theme.partner;

    return (
      <>
        <Switch>
          <Route path={routes.Bank} component={BanksSPA} />
        </Switch>

        <Switch>
          {/* Profiles */}
          <Route exact path={routes.GroupProfile} component={Group} />
          <Route exact path="/grupos/:profile/:tab" component={Group} />

          <Route exact path="/usuario/:alias/:tab(respuestas|articulos|reviews)" component={UserProfileLayout} />
          <Route exact path={routes.UserProfile} component={UserProfileLayout} />

          {/* Segmentos de contenido — rutas y redirects generados desde @finect/content-urls.
              Plano (hipotecas): basePath, /noticias, /:slug.
              Anidado (seguros): basePath (hub), /:vertical (artículo o sub-hub), /:vertical/:cluster (artículo).
              IMPORTANTE: todas exact y declaradas ANTES de routes.ProductSheet — 'seguros' es un
              product-type 'content', así que /seguros/:x casaría ProductSheet si se reordena. No mover. */}
          {Object.entries(SEGMENTS).flatMap(([key, config]) => {
            const legacy = Object.entries(config.legacyRedirects || {}).map(([from, to]) => (
              <Route
                key={`legacy-${from}`}
                exact
                path={from}
                render={props => <Redirect {...props} to={to} status={301} />}
              />
            ));

            if (config.verticals) {
              // Anidado — más específico primero.
              return [
                ...legacy,
                <Route
                  key={`${key}-cluster`}
                  exact
                  path={`${config.basePath}/:vertical/:cluster`}
                  render={props => <SegmentArticle {...props} segment={key} />}
                />,
                <Route
                  key={`${key}-vertical`}
                  exact
                  path={`${config.basePath}/:vertical`}
                  render={props => <SegmentVertical {...props} segment={key} />}
                />,
                <Route
                  key={`${key}-hub`}
                  exact
                  path={config.basePath}
                  render={props => <SegmentHub {...props} segment={key} />}
                />
              ];
            }

            // Plano.
            return [
              ...legacy,
              <Route
                key={`${key}-root`}
                exact
                path={config.basePath}
                render={props => <SegmentRoot {...props} segment={key} />}
              />,
              <Route
                key={`${key}-noticias`}
                exact
                path={`${config.basePath}/noticias`}
                render={props => <SegmentNoticias {...props} segment={key} />}
              />,
              <Route
                key={`${key}-article`}
                exact
                path={`${config.basePath}/:slug`}
                render={props => <SegmentArticle {...props} segment={key} />}
              />
            ];
          })}

          {/* Redirects /articulos/{slug} → segmento (generados desde @finect/content-urls) */}
          {Object.entries(CONTENT_URL_MAP).map(([slug, path]) => (
            <Route
              key={slug}
              exact
              path={`/articulos/${slug}`}
              render={props => <Redirect {...props} to={path} status={301} />}
            />
          ))}

          {/* Artículos */}
          <Route exact path={`/articulos/:article`} component={ArticleScroll} />
          <Route exact path="/:subtype(grupos|usuario)/:profile/articulos/:article" component={ArticleScroll} />
          <Route exact path="/articulos-financieros/noticias" render={props => <Latest type="noticias" {...props} />} />
          <Route exact path="/articulos-financieros/a-fondo" render={props => <Latest type="a-fondo" {...props} />} />
          <Route
            exact
            path="/articulos-financieros/en-un-minuto"
            render={props => <Latest type="en-un-minuto" {...props} />}
          />
          {/* Artículos - Redirects (and old links to /blog/, /blogs/ or /blogs-) */}
          {[
            '/articulos-financieros',
            '/articulos-financieros/ultimos',
            '/(blog|blogs)/',
            '/(blog|blogs)/*',
            '/blogs-*'
          ].map((path, index) => (
            <Route
              key={index}
              exact
              path={path}
              render={props => <Redirect {...props} to="/articulos-financieros/noticias" status={301} />}
            />
          ))}

          {/* Bancos */}
          <Route exact path={routes.BankArticle} component={BankArticle} />
          <Route exact path={routes.BankProducts} component={BankProducts} />
          <Route exact path={`${routes.Bank}/productos`} component={BankProducts} />
          <Route exact path={routes.Bank} component={Bank} />
          <Route exact path={routes.BankArticleList} component={BankArticleList} />

          {/* Escaparates */}
          <Route exact path={routes.StorefrontsHome} component={StorefrontsHome} />
          <Route exact path={routes.Storefront} component={Storefront} />
          <Route exact path={routes.StorefrontProductsList} component={StorefrontProductsList} />
          {/* Activos digitales */}
          <Route exact path={routes.DigitalHome} component={DigitalHome} />
          <Route exact path={`${routes.DigitalHome}/formacion`} component={DigitalAcademy} />
          {isPreRelease && <Route exact path="/activos-digitales/listado" component={CryptoList} />}
          {isPreRelease && <Route exact path="/activos-digitales/:productSlug" component={CryptoSheet} />}
          {/* Activos alternativos */}
          <Route exact path={routes.AlternativesHome} component={AlternativesHome} />
          <Route exact path={`${routes.AlternativesHome}/formacion`} component={AlternativesAcademy} />
          {/* Finanzas personales */}
          <Route exact path={routes.PersonalHome} component={PersonalHome} />
          {OTHER_TAGS.map((tag, index) => (
            <Route
              key={index}
              exact
              path={`${routes.PersonalHome}/:contentTag(${tag.slug})`}
              render={props => <PersonalTag {...props} contentTag={tag} />}
            />
          ))}

          {/* Comparador de productos que no son IIC */}
          <Route exact path={routes.ProductsComparison} render={props => <ProductComparison {...props} />} />

          {/* Redirects /criptos/ → /activos-digitales/ */}
          {isPreRelease && (
            <Route
              exact
              path="/criptos/:productSlug"
              render={props => (
                <Redirect {...props} status={301} to={`/activos-digitales/${props.match.params.productSlug}`} />
              )}
            />
          )}
          {isPreRelease && (
            <Route
              exact
              path="/criptos"
              render={props => <Redirect {...props} status={301} to="/activos-digitales/listado" />}
            />
          )}

          {/* Storefronts removidos — redirect /:tag → artículo de referencia (driven by STOREFRONT_REDIRECTS) */}
          {STOREFRONT_REDIRECTS.map(({ tag, to }) => (
            <Route
              key={`storefront-redirect-${tag}`}
              exact
              path={`/${tag}`}
              render={props => <Redirect {...props} status={301} to={to} />}
            />
          ))}

          {/* Fichas de producto */}
          <Route exact path={routes.ProductSheet} component={ProductSheet} />

          {/* Listados de preguntas */}
          <Route exact path={routes.ProductQuestions} component={ProductQuestions} />
          <Route exact path={routes.FundQuestions} component={ProductQuestions} />

          {/* Listados de reviews */}
          <Route exact path={routes.ProductReviews} component={ProductReviews} />
          <Route exact path={routes.FundReviews} component={ProductReviews} />

          {/* Creación de reviews */}
          <Route exact path={routes.ProductWriteReview} render={props => <WriteReview {...props} />} />
          <Route exact path={routes.FundWriteReview} render={props => <WriteReview {...props} />} />

          {/* Detalle de review */}
          <Route exact path={routes.ProductReview} render={props => <ProductReview {...props} />} />
          <Route exact path={routes.FundReview} render={props => <ProductReview {...props} />} />

          {/* Grupos */}
          <Route exact path="/grupos" component={Groups} />

          {/* Multimedia. El listado completo va con `archive`: la portada reparte entre destacado,
              imprescindibles y ultimos, y manda ahi a quien quiera verlo todo.
              OJO: el enrutado de CLIENTE es este. scenes/routes.js lo usa el servidor para el SSR y
              el loadInitialData, asi que una ruta nueva hay que declararla en los DOS sitios; con
              /videos/todos solo en scenes/routes.js la pagina salia vacia. */}
          <Route exact path="/videos" render={props => <MultimediaCommons type="video" {...props} />} />
          <Route exact path="/podcasts" render={props => <MultimediaCommons type="podcast" {...props} />} />
          <Route exact path="/videos/todos" render={props => <MultimediaCommons type="video" archive {...props} />} />
          <Route
            exact
            path="/podcasts/todos"
            render={props => <MultimediaCommons type="podcast" archive {...props} />}
          />

          {/* IICs */}
          <Route exact path={routes.FundsList} component={FundsList} />
          {/* IICs - Supercomparador (fondos-inversion) - Comparison (planes-pensiones, etfs) */}
          <Route exact path="/fondos-inversion/comparador" component={SuperComparison} />
          <Route exact path={routes.FundsComparison} component={FundsComparison} />

          {/* /categoria | /categoria/:tab llevan a FundCategories */}
          <Route exact path={routes.FundsCategories} component={FundsCategories} />
          <Route exact path={routes.FundsCategoriesTab} component={FundsCategories} />
          <Route exact path={routes.FundsCategory} component={FundsCategory} />
          {/* -- old routes.FundsCategory */}
          <Route exact path={routes.FundsCategory.replace('-:alias', '')} component={FundsCategory} />

          {/* Gestoras */}
          <Route exact path={routes.FundsManagementCompanies} component={FundsManagementCompanies} />
          <Route exact path={routes.FundsManagementCompany} component={FundsManagementCompany} />
          {/* -- old routes.FundsManagementCompany */}
          <Route exact path={routes.FundsManagementCompany.replace('-:alias', '')} component={FundsManagementCompany} />

          {/* Ficha de fondos — la clase activa se comunica vía `?clase={IdSecurity}`. */}
          <Route exact path={routes.FundSheet} component={FundSheet} />
          <Route exact path={routes.FundSheetById} component={FundSheetById} />

          {/* Homes de fondos de inversión y de planes de pensiones */}
          <Route exact path={routes.PlansHome} component={PlansHome} />
          <Route exact path={routes.ETFsHome} component={ETFsHome} />
          <Route exact path={routes.FundsHome} component={FundsHome} />

          {/* Home de Finect */}
          <Route exact path="/" component={Home} />

          {/* ············· Redirects home */}
          <Route
            exact
            path="/(pro|Home|home|homepage|index)"
            render={props => <Redirect {...props} to="/" status={301} />}
          />

          {/* Índices */}
          <Route exact path="/euribor" component={Euribor} />

          {/* Página de tags - Temas */}
          <Route exact path="/temas" component={TagsHome} />

          {/* Página de tag - Tema */}
          <Route exact path="/temas/:tag" render={props => <Tag {...props} />} />

          {/* Favoritos - OLD  */}
          <PrivateRoute exact path="/(mis|tus)-favoritos" from="/tus-favoritos" component={Favorites} />
          <PrivateRoute exact path="/(mis|tus)-finanzas" from="/tus-finanzas" component={Panel} />
          <PrivateRoute exact path="/(mis|tus)-temas" from="/tus-temas" component={Feed} />
          <PrivateRoute exact path="/(mis|tus)-finanzas/feed" from="/tus-finanzas/feed" component={Feed} />
          <PrivateRoute exact path="/tus-comparaciones" component={MyComparisons} />
          <PrivateRoute exact path="/tus-autorizaciones" component={MyAuthorizations} />

          {/* Finect Plus. PlusRoute = sesión + suscripción; las excepciones que no exigen suscripción
              van marcadas con un comentario. */}
          <PlusRoute exact path="/plus/seleccion" component={MySelection} />
          <PlusRoute exact path="/plus/seleccion/:section" component={MySelectionSection} />
          <PlusRoute exact path="/plus/herramientas" component={MyTools} />
          <PlusRoute exact path="/plus/herramientas/:section" component={MyTools} />
          <PlusRoute exact path="/plus/analisis" component={MyAnalysis} />
          <PlusRoute exact path="/plus/comparador" component={MySuperComparison} />
          <PlusRoute exact path="/plus/micartera" component={MyPortfolio} />
          <Route
            exact
            path="/plus/cartera"
            render={props => <Redirect {...props} to={`/plus/micartera${props.location.search}`} status={301} />}
          />
          <PlusRoute exact path="/plus/novedades" component={VersionNotes} />
          {isPreRelease && <PlusRoute exact path="/plus/chatbot/:conversationId?" component={PlusChatbot} />}

          {/* Carteras públicas: viven bajo /plus por diseño (navegas en el layout de Plus)
              pero son rutas públicas (Route, no PrivateRoute); el detalle compartible se
              mantiene en top-level /carteras/:token para no dar señal de "privado" a quien
              recibe el enlace. El index/noindex lo controla el meta robots de cada página. */}
          <Route exact path="/plus/carteras" component={SponsoredPortfolios} />
          <Route exact path="/carteras/:token" component={SharedPortfolio} />

          {/* La FAQ de Plus explica qué es la suscripción a quien todavía no la tiene: pública,
              sin sesión ni suscripción. */}
          <Route exact path="/plus/preguntas-frecuentes" component={PlusFAQ} />

          {/* Redirects 301 de las URLs antiguas → nuevas */}
          <Route
            exact
            path="/carteras-modelo"
            render={props => <Redirect {...props} to={`/plus/carteras${props.location.search}`} status={301} />}
          />
          <Route
            exact
            path="/cartera/:token"
            render={props => (
              <Redirect {...props} to={`/carteras/${props.match.params.token}${props.location.search}`} status={301} />
            )}
          />

          {/* ············· Redirects Favoritos (borrar el primero y descomentar los otros cuando salga Finect Plus) */}
          <Route
            exact
            path="/(mis|tus)-productos"
            render={props => <Redirect {...props} to="/tus-favoritos" status={301} />}
          />
          {/* <Route
            exact
            path="/(mis|tus)-productos"
            render={props => <Redirect {...props} to="/mi-seleccion#productos" status={301} />}
          />
          <Route
            exact
            path="/(mis|tus)-favoritos"
            render={props => <Redirect {...props} to="/mi-seleccion#productos" status={301} />}
          />
          <Route
            exact
            path="/(mis|tus)-finanzas"
            render={props => <Redirect {...props} to="/mi-herramienta#perfil-financiero" status={301} />}
          /> */}

          {/* Escenas privadas de Onboarding  */}
          <Route exact path="/onboarding/start" component={Start} />
          <PrivateRoute exact path="/onboarding" component={OnBoarding} />

          {/* Valoración asesores */}
          <PrivateRoute exact path="/tus-finanzas/asesores/valoracion/:lead" component={AdvisersFeedback} />
          <PrivateRoute exact path="/tus-finanzas/asesores/valoracion" component={AdvisersFeedback} />

          {/* ProductSubscription */}
          <Route
            exact
            path={`/:productTypeSlug(servicios)/:productSlug(finect-subscription)/comprar`}
            component={ProductSubscription}
          />
          <Route
            exact
            path={`/:productTypeSlug(servicios)/:productSlug(finect-subscription)/condiciones`}
            component={SubscriptionConditions}
          />

          <Route
            exact
            path={`/:productTypeSlug(servicios)/:productSlug(finect-plus)/comprar`}
            component={ProductSubscription}
          />

          <Route
            exact
            path={`/:productTypeSlug(servicios)/:productSlug(finect-plus)/condiciones`}
            component={PlusSubscriptionConditions}
          />

          {/* ProductCheckout */}
          {/* <Route exact path={routes.ProductCheckout} component={ProductCheckout} /> */}

          <Route component={NotFound} />
        </Switch>
      </>
    );
  }
}

export default withTheme(AppRouter);
